import React from "react";
import { Box, Typography } from "@mui/material";
import { AccessTime, LocationOn, People } from "@mui/icons-material";
import { BaseActionRendererProps, CalendarData, getActionData } from "./BaseActionRenderer";

export const CalendarActionRenderer: React.FC<BaseActionRendererProps> = ({ actionData }) => {
  const calendarData = getActionData(actionData) as CalendarData;

  const title = calendarData.title || "Calendar Event";
  const description = calendarData.description;
  const location = calendarData.location;
  const startTime = calendarData.startTime;
  const endTime = calendarData.endTime;
  const timezone = calendarData.timezone;
  const attendees = calendarData.attendees;

  // Format dates for display
  const formatDateTime = (dateTime: string, tz?: string) => {
    if (!dateTime) return "";

    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return dateTime;

    // Use user timezone if available, otherwise use event timezone or default
    const displayTz = tz || "UTC";

    try {
      return date.toLocaleString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: displayTz,
      });
    } catch (error) {
      return dateTime;
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
        {title}
      </Typography>

      {/* Description */}
      {description && (
        <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
          {description}
        </Typography>
      )}

      {/* Time information */}
      {(startTime || endTime) && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <AccessTime sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Time:
            </Typography>
          </Box>
          <Box sx={{ ml: 3 }}>
            {startTime && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <b>Start:</b> {formatDateTime(startTime, timezone)}
              </Typography>
            )}
            {endTime && (
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <b>End:</b> {formatDateTime(endTime, timezone)}
              </Typography>
            )}
            {timezone && (
              <Typography variant="caption" color="text.secondary">
                Timezone: {timezone}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Location information */}
      {location && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <LocationOn sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Location:
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ ml: 3 }}>
            {location}
          </Typography>
        </Box>
      )}

      {/* Attendees information */}
      {attendees && attendees.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <People sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Attendees:
            </Typography>
          </Box>
          <Box sx={{ ml: 3 }}>
            {attendees.map((attendee, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                {attendee.displayName ? `${attendee.displayName} (${attendee.email})` : attendee.email}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {actionData.reason && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mt: 1 }}>
          Reason: {actionData.reason}
        </Typography>
      )}
    </Box>
  );
};
